import { useEffect, useState } from "react";
import { api, errorMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Replace, Package, AlertTriangle, Sparkles, Loader2 } from "lucide-react";

export const EquivalentesDialog = ({ trabajoId, solicitud, open, onClose, onSustituido }) => {
  const [cargando, setCargando] = useState(false);
  const [data, setData] = useState(null);
  const [recordar, setRecordar] = useState(false);
  const [sustituyendo, setSustituyendo] = useState(null);

  useEffect(() => {
    if (!open || !solicitud) return;
    setData(null); setRecordar(false); setCargando(true);
    api.get(`/trabajos/${trabajoId}/solicitudes/${solicitud.id}/equivalentes`, { timeout: 120000 })
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(errorMsg(e)))
      .finally(() => setCargando(false));
  }, [open, solicitud, trabajoId]);

  const sustituir = async (op) => {
    setSustituyendo(op.codigo_parte);
    try {
      const { data: trabajo } = await api.post(
        `/trabajos/${trabajoId}/solicitudes/${solicitud.id}/sustituir`,
        { repuesto_id: op.repuesto_id || null, codigo: op.codigo_parte, nombre: op.nombre, recordar_equivalente: recordar });
      toast.success(`Sustituido por ${op.codigo_parte}`);
      onSustituido(trabajo);
      onClose();
    } catch (e) { toast.error(errorMsg(e)); }
    finally { setSustituyendo(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v && !sustituyendo) onClose(); }}>
      <DialogContent data-testid="equivalentes-dialog" className="bg-white max-w-2xl max-h-[90vh] flex flex-col">
        <DialogHeader className="shrink-0">
          <DialogTitle className="flex items-center gap-2"><Replace size={18} className="text-indigo-600" /> Repuestos equivalentes</DialogTitle>
          {data?.solicitado && (
            <p className="text-xs text-muted-foreground">
              Solicitado: <span className="font-mono font-semibold">{data.solicitado.codigo || "—"}</span> · {data.solicitado.nombre}
              {data.solicitado.modelo ? <> · Vehículo: <b>{data.solicitado.modelo}</b></> : null}
              {data.solicitado.cantidad ? <> · Cant.: {data.solicitado.cantidad}</> : null}
            </p>
          )}
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto pr-1 space-y-2">
          {cargando && (
            <div className="py-10 text-center text-sm text-muted-foreground flex items-center justify-center gap-2">
              <Loader2 size={16} className="animate-spin" /> Buscando equivalentes y consultando la IA…
            </div>
          )}
          {!cargando && data && data.opciones.length === 0 && (
            <p data-testid="equivalentes-vacio" className="py-10 text-center text-sm text-muted-foreground">
              No se encontraron repuestos equivalentes en el catálogo de modelos compatibles.
            </p>
          )}
          {!cargando && data && data.opciones.map((op, idx) => (
            <div key={op.codigo_parte} data-testid={`equivalente-${idx}`}
              className="border border-slate-200 rounded-sm p-2.5 flex items-start gap-3 hover:border-indigo-300">
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-mono font-semibold text-sm">{op.codigo_parte}</span>
                  <span className="text-sm text-slate-700">{op.nombre}</span>
                  {op.en_inventario
                    ? <span className="text-[10px] bg-teal-100 text-teal-700 px-1.5 py-0.5 rounded-sm font-medium inline-flex items-center gap-1"><Package size={10} /> en inventario</span>
                    : <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-sm font-medium">se creará</span>}
                  {op.definido_usuario && (
                    <span data-testid={`equivalente-${idx}-definido`} className="text-[10px] bg-violet-100 text-violet-700 px-1.5 py-0.5 rounded-sm font-medium">
                      equivalencia definida por vos
                    </span>
                  )}
                </div>
                {op.modelos?.length > 0 && (
                  <p className="text-[11px] text-slate-500 mt-0.5">Modelos compatibles: {op.modelos.join(", ")}</p>
                )}
                {op.nota && (
                  <p data-testid={`equivalente-${idx}-nota`} className="text-[11px] text-indigo-700 bg-indigo-50 border border-indigo-100 rounded-sm px-1.5 py-1 mt-1 flex items-start gap-1">
                    <Sparkles size={11} className="mt-0.5 shrink-0" /> {op.nota}
                  </p>
                )}
              </div>
              <Button size="sm" data-testid={`equivalente-${idx}-seleccionar`} disabled={!!sustituyendo}
                onClick={() => sustituir(op)} className="shrink-0 bg-indigo-600 hover:bg-indigo-700 text-white">
                {sustituyendo === op.codigo_parte ? <Loader2 size={13} className="animate-spin" /> : "Sustituir"}
              </Button>
            </div>
          ))}
        </div>

        <div className="shrink-0 border-t border-slate-100 pt-2 flex items-center justify-between gap-2">
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Checkbox data-testid="equivalentes-recordar" checked={recordar} onCheckedChange={setRecordar} />
            Recordar como equivalente (guardar el par para próximas veces)
          </label>
          <p className="text-[11px] text-amber-700 flex items-center gap-1">
            <AlertTriangle size={11} /> Compatibilidad a nivel de motor. La decisión final es suya.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};
